import React, { useState, useRef } from 'react';
import { toast } from 'react-toastify';
import instanciaAxios, { esErrorPermiso } from '../../miscelaneo/instanciaAxios';

const TAMANO_LOTE = 200;

const ETIQUETA = {
  lista: { texto: 'Lista para importar', clase: 'bg-blue-100 text-blue-800' },
  importada: { texto: 'Importada', clase: 'bg-green-100 text-green-800' },
  duplicada: { texto: 'Ya importada', clase: 'bg-gray-100 text-gray-700' },
  error: { texto: 'Con error', clase: 'bg-red-100 text-red-800' },
};

const formatoMonto = (n) =>
  Number(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Envia el archivo por lotes para no exceder el limite del cuerpo de la peticion.
async function enviarPorLotes(archivo, { dryRun, registrarPagos }) {
  const resultados = [];
  for (let i = 0; i < archivo.facturas.length; i += TAMANO_LOTE) {
    const lote = { ...archivo, facturas: archivo.facturas.slice(i, i + TAMANO_LOTE) };
    const { data } = await instanciaAxios.post('/lite/importar', {
      archivo: lote,
      dry_run: dryRun,
      registrar_pagos: registrarPagos,
    });
    resultados.push(...data.resultados);
  }
  return resultados;
}

const ImportarFacturasLite = () => {
  const entradaArchivo = useRef(null);
  const [archivo, setArchivo] = useState(null);
  const [nombre, setNombre] = useState('');
  const [resultados, setResultados] = useState(null);
  const [registrarPagos, setRegistrarPagos] = useState(true);
  const [procesando, setProcesando] = useState(false);
  const [yaImportado, setYaImportado] = useState(false);

  const reiniciar = () => {
    setArchivo(null);
    setNombre('');
    setResultados(null);
    setYaImportado(false);
    if (entradaArchivo.current) entradaArchivo.current.value = '';
  };

  const alElegirArchivo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setResultados(null);
    setYaImportado(false);
    try {
      const json = JSON.parse(await f.text());
      if (json?.origen !== 'omnierp-lite' || !Array.isArray(json.facturas)) {
        throw new Error('No es un archivo exportado por OmniERP Lite');
      }
      setArchivo(json);
      setNombre(f.name);
      await revisar(json, registrarPagos);
    } catch (err) {
      setArchivo(null);
      toast.error(err.response?.data?.mensaje || err.message || 'No se pudo leer el archivo');
    }
  };

  const revisar = async (json, pagos) => {
    setProcesando(true);
    try {
      setResultados(await enviarPorLotes(json, { dryRun: true, registrarPagos: pagos }));
    } catch (err) {
      if (esErrorPermiso(err)) return;
      setResultados(null);
      toast.error(err.response?.data?.mensaje || 'No se pudo revisar el archivo');
    } finally {
      setProcesando(false);
    }
  };

  const importar = async () => {
    setProcesando(true);
    try {
      const res = await enviarPorLotes(archivo, { dryRun: false, registrarPagos });
      setResultados(res);
      setYaImportado(true);
      const ok = res.filter((r) => r.estado === 'importada').length;
      const err = res.filter((r) => r.estado === 'error').length;
      if (err === 0) toast.success(`${ok} factura(s) importada(s)`);
      else toast.warning(`${ok} importada(s), ${err} con error. Revise el detalle.`);
    } catch (err) {
      if (esErrorPermiso(err)) return;
      toast.error(err.response?.data?.mensaje || 'No se pudo importar el archivo');
    } finally {
      setProcesando(false);
    }
  };

  const cuenta = (estado) => (resultados || []).filter((r) => r.estado === estado).length;
  const listas = cuenta('lista');

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-800">Importar facturas de OmniERP Lite</h1>
      <p className="text-sm text-gray-600 mt-1">
        Sube el archivo exportado desde OmniERP Lite. Las facturas se crean como <b>facturas demo</b> (sin comprobante),
        con inventario y pagos, y luego pueden convertirse a fiscales. Una factura ya importada no se duplica.
      </p>

      <div className="bg-white rounded-xl border shadow-md p-4 mt-4 space-y-3">
        <input ref={entradaArchivo} type="file" accept=".json,application/json" onChange={alElegirArchivo} disabled={procesando} />
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={registrarPagos}
            disabled={procesando || yaImportado}
            onChange={(e) => { setRegistrarPagos(e.target.checked); if (archivo) revisar(archivo, e.target.checked); }}
          />
          Registrar los pagos de las ventas (requiere tener la caja abierta)
        </label>
        {archivo && (
          <p className="text-xs text-gray-500">
            {nombre} · {archivo.facturas.length} factura(s) · exportado {new Date(archivo.exportado_en).toLocaleString()}
          </p>
        )}
      </div>

      {resultados && (
        <div className="bg-white rounded-xl border shadow-md p-4 mt-4">
          <div className="flex flex-wrap gap-4 text-sm mb-3">
            <span>Listas: <b>{cuenta('lista')}</b></span>
            <span>Importadas: <b>{cuenta('importada')}</b></span>
            <span>Ya importadas: <b>{cuenta('duplicada')}</b></span>
            <span>Con error: <b className={cuenta('error') ? 'text-red-600' : ''}>{cuenta('error')}</b></span>
          </div>
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-1 pr-2">Número</th><th className="pr-2">Estado</th>
                  <th className="pr-2 text-right">Total</th><th>Detalle</th>
                </tr>
              </thead>
              <tbody>
                {resultados.map((r, i) => (
                  <tr key={r.uuid_local || i} className="border-b last:border-0">
                    <td className="py-1 pr-2">{r.numero || '-'}</td>
                    <td className="pr-2">
                      <span className={`px-2 py-0.5 rounded text-xs ${ETIQUETA[r.estado].clase}`}>{ETIQUETA[r.estado].texto}</span>
                    </td>
                    <td className="pr-2 text-right">{r.total != null ? formatoMonto(r.total) : ''}</td>
                    <td className="text-gray-600">
                      {r.mensaje || (r.id_factura ? `Factura #${r.id_factura}` : '')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex gap-2 mt-4">
            {!yaImportado && (
              <button
                className="px-4 py-2 rounded bg-green-600 text-white disabled:opacity-50"
                disabled={procesando || listas === 0}
                onClick={importar}
              >
                {procesando ? 'Procesando...' : `Importar ${listas} factura(s)`}
              </button>
            )}
            <button className="px-4 py-2 rounded bg-gray-200 text-gray-800" disabled={procesando} onClick={reiniciar}>
              {yaImportado ? 'Importar otro archivo' : 'Cancelar'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportarFacturasLite;
